import { DEG } from "@/core/math/scalar";
import { CelestialBodyDef } from "../BodyDef";
import { HOURS, orbitAU } from "./orbitHelpers";

/**
 * The nine major planets of the Cyrene system.
 *
 * Each entry is a complete, self-contained description: orbit, rotation,
 * atmosphere, thermal profile, hazards, terrain generation rules, colour
 * palette, biome set and resource abundances. Adding a tenth planet means
 * appending one object here — no engine change is required, which is the point
 * of keeping all of this out of code.
 *
 * The terrain profiles are deliberately dissimilar. Vesper is fissure-dominated
 * with almost no fractal relief; Kharis is dune-dominated with wide flat
 * basins; Orphys is tectonically violent with a small relief budget relative to
 * its radius; Kolyma is a low-relief ice shell scarred by ancient impacts. They
 * do not share a template with different multipliers.
 */

// ---------------------------------------------------------------------------
// 1. Vesper - volcanic inner world
// ---------------------------------------------------------------------------
const VESPER: CelestialBodyDef = {
  id: "vesper",
  name: "Vesper",
  kind: "planet",
  parentId: "cyrene",
  radius: 5.21e6,
  surfaceGravity: 8.74,
  rotationPeriod: 84.2 * HOURS,
  axialTilt: 2.6 * DEG,
  epochSpin: 0.4,
  orbit: orbitAU(0.381, 0.048, 6.2, 41.0, 88.0, 210.0),
  surfaceClass: "volcanic",
  explorable: true,
  atmosphere: {
    surfaceDensity: 1.42,
    scaleHeight: 9800,
    surfacePressure: 92000,
    topAltitude: 96000,
    molarMass: 0.058,
    composition: { CO2: 0.71, SO2: 0.19, N2: 0.07, H2S: 0.03 },
    breathable: false,
    rayleigh: [7.2e-6, 4.4e-6, 2.1e-6],
    mie: 3.4e-5,
    mieAnisotropy: 0.76,
    hazeColor: [0.86, 0.42, 0.19],
    windSpeed: 14,
    turbulence: 0.55,
    cloudCoverage: 0.18,
    cloudAltitude: 9000,
    cloudColor: [0.62, 0.52, 0.44],
  },
  thermal: {
    meanSurfaceTemperature: 611,
    polarDelta: 74,
    diurnalRange: 96,
    lapseRate: 0.0092,
  },
  hazards: {
    radiation: 0.0021,
    stormFrequency: 0.62,
    seismicActivity: 0.94,
    corrosion: 3.4,
    notes: [
      "Sulphuric aerosols corrode unshielded hulls within hours.",
      "Active fissure eruptions relocate on a scale of days.",
      "Surface temperature exceeds the rating of standard life support.",
    ],
  },
  terrain: {
    maxElevation: 6200,
    seaLevelFraction: -1,
    oceanCoverage: 0,
    continentScale: 1400000,
    plateCount: 4,
    ridgeStrength: 0.42,
    mountainStrength: 0.36,
    mountainScale: 180000,
    warpStrength: 0.22,
    detailStrength: 0.3,
    detailScale: 900,
    erosion: 0.08,
    craterDensity: 0.05,
    craterMaxRadius: 12000,
    duneStrength: 260,
    duneScale: 2100,
    fissureStrength: 0.85,
    roughness: 0.25,
  },
  palette: {
    deepWater: [0.42, 0.09, 0.02],
    shallowWater: [0.72, 0.2, 0.03],
    shore: [0.24, 0.13, 0.1],
    lowland: [0.16, 0.11, 0.1],
    midland: [0.27, 0.19, 0.16],
    highland: [0.38, 0.28, 0.23],
    peak: [0.55, 0.46, 0.41],
    nightEmissive: [1.0, 0.32, 0.05],
    nightEmissiveStrength: 0.85,
  },
  biomes: ["obsidianFlow", "ashPlain", "volcanic", "crystalField", "cave"],
  settlementDensity: 0.12,
  lifePotential: 0.04,
  description:
    "A tidally slowed inner world resurfaced continuously by fissure volcanism. Its crust is only tens of " +
    "millions of years old anywhere. Industrial interest is entirely in sulphur, refractory metals and the " +
    "exotic crystals that grow in cooled lava tubes.",
  markerColor: [0.94, 0.42, 0.18],
};

// ---------------------------------------------------------------------------
// 2. Ashkar - toxic greenhouse world
// ---------------------------------------------------------------------------
const ASHKAR: CelestialBodyDef = {
  id: "ashkar",
  name: "Ashkar",
  kind: "planet",
  parentId: "cyrene",
  radius: 6.02e6,
  surfaceGravity: 8.98,
  rotationPeriod: -241 * HOURS,
  axialTilt: 177.2 * DEG,
  epochSpin: 2.1,
  orbit: orbitAU(0.682, 0.011, 3.1, 76.0, 54.0, 55.0),
  surfaceClass: "toxic",
  explorable: true,
  atmosphere: {
    surfaceDensity: 48.0,
    scaleHeight: 15600,
    surfacePressure: 8.6e6,
    topAltitude: 180000,
    molarMass: 0.0435,
    composition: { CO2: 0.94, N2: 0.035, SO2: 0.015, HF: 0.01 },
    breathable: false,
    rayleigh: [1.1e-5, 9.4e-6, 5.6e-6],
    mie: 9.1e-5,
    mieAnisotropy: 0.82,
    hazeColor: [0.94, 0.78, 0.36],
    windSpeed: 92,
    turbulence: 0.7,
    cloudCoverage: 0.2,
    cloudAltitude: 26000,
    cloudColor: [0.92, 0.86, 0.62],
  },
  thermal: {
    meanSurfaceTemperature: 742,
    polarDelta: 12,
    diurnalRange: 6,
    lapseRate: 0.0081,
  },
  hazards: {
    radiation: 0.0004,
    stormFrequency: 0.88,
    seismicActivity: 0.31,
    corrosion: 9.6,
    notes: [
      "Surface pressure crushes standard pressure vessels below 20 km altitude.",
      "Hydrofluoric aerosols destroy most seals and optics.",
      "Super-rotating winds exceed 90 m/s in the upper haze deck.",
      "Cloud-deck aerostats are the only sustainable habitation.",
    ],
  },
  terrain: {
    maxElevation: 9400,
    seaLevelFraction: -1,
    oceanCoverage: 0,
    continentScale: 2.6e6,
    plateCount: 8,
    ridgeStrength: 0.24,
    mountainStrength: 0.22,
    mountainScale: 3.4e5,
    warpStrength: 0.35,
    detailStrength: 0.12,
    detailScale: 2400,
    erosion: 0.55,
    craterDensity: 0.02,
    craterMaxRadius: 40000,
    duneStrength: 10,
    duneScale: 5200,
    fissureStrength: 0.18,
    roughness: 0.62,
  },
  palette: {
    deepWater: [0.3, 0.22, 0.1],
    shallowWater: [0.45, 0.34, 0.15],
    shore: [0.16, 0.1, 0.07],
    lowland: [0.12, 0.08, 0.06],
    midland: [0.5, 0.32, 0.14],
    highland: [0.7, 0.58, 0.34],
    peak: [0.86, 0.8, 0.58],
    nightEmissive: [0.5, 0.15, 0.02],
    nightEmissiveStrength: 0.12,
  },
  biomes: ["toxicWaste", "corrosionScar", "sulfurCrust"],
  settlementDensity: 0.08,
  lifePotential: 0.01,
  description:
    "A retrograde-rotating greenhouse world with a surface hot enough to glow faintly in the infrared. Almost " +
    "all human presence is airborne: buoyant stations float at the 50 km level where pressure and temperature " +
    "are survivable and the rare-earth-rich haze can be harvested directly.",
  markerColor: [0.93, 0.8, 0.36],
};

// ---------------------------------------------------------------------------
// 3. Halcyon - temperate terrestrial, the starting world
// ---------------------------------------------------------------------------
const HALCYON: CelestialBodyDef = {
  id: "halcyon",
  name: "Halcyon",
  kind: "planet",
  parentId: "cyrene",
  radius: 6.11e6,
  surfaceGravity: 9.41,
  rotationPeriod: 22.63 * HOURS,
  axialTilt: 21.4 * DEG,
  epochSpin: 1.2,
  orbit: orbitAU(1.0, 0.0163, 0.0, 0.0, 102.0, 128.0),
  surfaceClass: "terrestrial",
  explorable: true,
  atmosphere: {
    surfaceDensity: 1.19,
    scaleHeight: 8600,
    surfacePressure: 103000,
    topAltitude: 105000,
    molarMass: 0.0289,
    composition: { N2: 0.765, O2: 0.211, Ar: 0.015, CO2: 0.009 },
    breathable: true,
    rayleigh: [5.8e-6, 1.35e-5, 3.31e-5],
    mie: 2.1e-5,
    mieAnisotropy: 0.76,
    hazeColor: [0.55, 0.68, 0.9],
    windSpeed: 7,
    turbulence: 0.28,
    cloudCoverage: 0.52,
    cloudAltitude: 4200,
    cloudColor: [1.0, 1.0, 1.0],
  },
  thermal: {
    meanSurfaceTemperature: 292,
    polarDelta: 58,
    diurnalRange: 14,
    lapseRate: 0.0065,
  },
  hazards: {
    radiation: 0.00002,
    stormFrequency: 0.34,
    seismicActivity: 0.4,
    corrosion: 0,
    notes: [
      "Breathable atmosphere; no pressure suit required below 5 km.",
      "Seasonal cyclones in the equatorial ocean basins.",
      "Polar regions drop below survivable temperatures without heating.",
    ],
  },
  terrain: {
    maxElevation: 8900,
    seaLevelFraction: -0.08,
    oceanCoverage: 0.63,
    continentScale: 2100000,
    plateCount: 17,
    ridgeStrength: 0.78,
    mountainStrength: 0.62,
    mountainScale: 240000,
    warpStrength: 0.4,
    detailStrength: 0.12,
    detailScale: 3630,
    erosion: 0.62,
    craterDensity: 0.004,
    craterMaxRadius: 18000,
    duneStrength: 18,
    duneScale: 3200,
    fissureStrength: 0.05,
    roughness: 1.35,
  },
  palette: {
    deepWater: [0.012, 0.045, 0.11],
    shallowWater: [0.05, 0.24, 0.34],
    shore: [0.62, 0.57, 0.41],
    lowland: [0.19, 0.29, 0.13],
    midland: [0.24, 0.3, 0.17],
    highland: [0.38, 0.35, 0.29],
    peak: [0.86, 0.88, 0.92],
    nightEmissive: [1.0, 0.78, 0.42],
    nightEmissiveStrength: 0.35,
  },
  biomes: [
    "ocean",
    "coastal",
    "temperateForest",
    "grassland",
    "savannah",
    "swamp",
    "alpine",
    "tundra",
    "glacier",
    "rockyDesert",
    "cave",
  ],
  settlementDensity: 0.85,
  lifePotential: 0.95,
  description:
    "The system's only unambiguously habitable world and the origin of every settlement beyond it. Two large " +
    "continental masses straddle a warm equatorial ocean; a third, colder landmass wraps the southern pole. " +
    "Most of what the frontier eats, breathes and builds with still leaves from here.",
  markerColor: [0.36, 0.66, 0.86],
};

// ---------------------------------------------------------------------------
// 4. Thalassa - oceanic world
// ---------------------------------------------------------------------------
const THALASSA: CelestialBodyDef = {
  id: "thalassa",
  name: "Thalassa",
  kind: "planet",
  parentId: "cyrene",
  radius: 7.04e6,
  surfaceGravity: 10.62,
  rotationPeriod: 19.1 * HOURS,
  axialTilt: 16.8 * DEG,
  epochSpin: 5.0,
  orbit: orbitAU(1.312, 0.0212, 1.8, 128.0, 12.0, 301.0),
  surfaceClass: "oceanic",
  explorable: true,
  atmosphere: {
    surfaceDensity: 1.86,
    scaleHeight: 9200,
    surfacePressure: 164000,
    topAltitude: 120000,
    molarMass: 0.0301,
    composition: { N2: 0.82, O2: 0.14, H2O: 0.03, CO2: 0.01 },
    breathable: false,
    rayleigh: [6.6e-6, 1.52e-5, 3.6e-5],
    mie: 4.4e-5,
    mieAnisotropy: 0.79,
    hazeColor: [0.44, 0.7, 0.86],
    windSpeed: 21,
    turbulence: 0.20,
    cloudCoverage: 0.35,
    cloudAltitude: 4400,
    cloudColor: [1.0, 0.99, 0.98],
  },
  thermal: {
    meanSurfaceTemperature: 281,
    polarDelta: 41,
    diurnalRange: 5,
    lapseRate: 0.0058,
  },
  hazards: {
    radiation: 0.00003,
    stormFrequency: 0.81,
    seismicActivity: 0.52,
    corrosion: 0.3,
    notes: [
      "Oxygen fraction is too low to breathe unassisted.",
      "Standing waves exceeding 30 m during storm season.",
      "Only nine permanent islands rise above the mean waterline.",
    ],
  },
  terrain: {
    maxElevation: 7100,
    seaLevelFraction: 0.34,
    oceanCoverage: 0.94,
    continentScale: 1.1e6,
    plateCount: 26,
    ridgeStrength: 0.86,
    mountainStrength: 0.7,
    mountainScale: 1.6e5,
    warpStrength: 0.5,
    detailStrength: 0.22,
    detailScale: 1100,
    erosion: 0.74,
    craterDensity: 0,
    craterMaxRadius: 1,
    duneStrength: 6,
    duneScale: 900,
    fissureStrength: 0.22,
    roughness: 1.1,
  },
  palette: {
    deepWater: [0.005, 0.03, 0.09],
    shallowWater: [0.03, 0.3, 0.4],
    shore: [0.72, 0.68, 0.58],
    lowland: [0.2, 0.33, 0.22],
    midland: [0.26, 0.31, 0.24],
    highland: [0.4, 0.4, 0.38],
    peak: [0.78, 0.82, 0.86],
    nightEmissive: [0.4, 0.9, 1.0],
    nightEmissiveStrength: 0.22,
  },
  rings: {
    innerRadius: 1.4e7,
    outerRadius: 2.42e7,
    color: [0.26, 0.24, 0.2],
    opacity: 0.75,
  },
  biomes: [
    "ocean",
    "coastal",
    "rainforest",
    "swamp",
    "alpine",
    "alienOrganic",
    "cave",
  ],
  settlementDensity: 0.34,
  lifePotential: 0.88,
  description:
    "A world drowned by its own water inventory. A global ocean averaging eleven kilometres deep covers all but " +
    "the highest volcanic ridges. Its ecosystems are almost entirely aquatic, and its economy runs on floating " +
    "harvest platforms and deep-basin extraction.",
  markerColor: [0.24, 0.56, 0.82],
};

// ---------------------------------------------------------------------------
// 5. Kharis - arid desert world
// ---------------------------------------------------------------------------
const KHARIS: CelestialBodyDef = {
  id: "kharis",
  name: "Kharis",
  kind: "planet",
  parentId: "cyrene",
  radius: 4.32e6,
  surfaceGravity: 5.61,
  rotationPeriod: 26.4 * HOURS,
  axialTilt: 14.1 * DEG,
  epochSpin: 3.3,
  orbit: orbitAU(1.724, 0.0891, 2.4, 49.0, 286.0, 19.0),
  surfaceClass: "desert",
  explorable: true,
  atmosphere: {
    surfaceDensity: 0.031,
    scaleHeight: 11400,
    surfacePressure: 2900,
    topAltitude: 92000,
    molarMass: 0.0434,
    composition: { CO2: 0.93, N2: 0.045, Ar: 0.02, O2: 0.005 },
    breathable: false,
    rayleigh: [1.9e-5, 1.4e-5, 8.2e-6],
    mie: 1.4e-5,
    mieAnisotropy: 0.7,
    hazeColor: [0.86, 0.62, 0.42],
    windSpeed: 11,
    turbulence: 0.42,
    cloudCoverage: 0.08,
    cloudAltitude: 7000,
    cloudColor: [0.86, 0.74, 0.58],
  },
  thermal: {
    meanSurfaceTemperature: 232,
    polarDelta: 66,
    diurnalRange: 78,
    lapseRate: 0.0045,
  },
  hazards: {
    radiation: 0.00042,
    stormFrequency: 0.58,
    seismicActivity: 0.09,
    corrosion: 0.1,
    notes: [
      "Global dust storms can persist for weeks and black out solar generation.",
      "Thin atmosphere gives almost no aerodynamic braking on entry.",
      "Night-side temperatures fall below 160 K.",
    ],
  },
  terrain: {
    maxElevation: 23500,
    seaLevelFraction: -1,
    oceanCoverage: 0.65,
    continentScale: 1700000,
    plateCount: 5,
    ridgeStrength: 0.16,
    mountainStrength: 0.54,
    mountainScale: 190000,
    warpStrength: 0.28,
    detailStrength: 0.86,
    detailScale: 1820,
    erosion: 0.36,
    craterDensity: 0.42,
    craterMaxRadius: 33000,
    duneStrength: 30,
    duneScale: 7450,
    fissureStrength: 0.12,
    roughness: 0.07,
  },
  palette: {
    deepWater: [0.2, 0.12, 0.08],
    shallowWater: [0.34, 0.22, 0.14],
    shore: [0.62, 0.44, 0.29],
    lowland: [0.58, 0.36, 0.22],
    midland: [0.66, 0.44, 0.28],
    highland: [0.5, 0.35, 0.26],
    peak: [0.78, 0.7, 0.62],
    nightEmissive: [1.0, 0.72, 0.4],
    nightEmissiveStrength: 0.14,
  },
  biomes: [
    "desert",
    "dunes",
    "rockyDesert",
    "saltFlat",
    "karst",
    "cave",
    "tundra",
  ],
  settlementDensity: 0.46,
  lifePotential: 0.18,
  description:
    "The frontier's workhorse. Low gravity and a thin atmosphere make Kharis cheap to launch from, and its iron " +
    "oxide plains and buried polar ice have sustained mining colonies for three generations. Nothing grows " +
    "outside a greenhouse, but almost everything is built here.",
  markerColor: [0.86, 0.46, 0.26],
};

// ---------------------------------------------------------------------------
// 6. Orphys - rocky high-gravity world
// ---------------------------------------------------------------------------
const ORPHYS: CelestialBodyDef = {
  id: "orphys",
  name: "Orphys",
  kind: "planet",
  parentId: "cyrene",
  radius: 9.86e6,
  surfaceGravity: 22.4,
  rotationPeriod: 41.7 * HOURS,
  axialTilt: 8.9 * DEG,
  epochSpin: 0.9,
  orbit: orbitAU(2.441, 0.0344, 4.9, 202.0, 141.0, 264.0),
  surfaceClass: "rocky",
  explorable: true,
  atmosphere: {
    surfaceDensity: 3.4,
    scaleHeight: 5200,
    surfacePressure: 310000,
    topAltitude: 74000,
    molarMass: 0.0402,
    composition: { N2: 0.62, CO2: 0.31, Ar: 0.06, CH4: 0.01 },
    breathable: false,
    rayleigh: [8.4e-6, 1.1e-5, 1.8e-5],
    mie: 5.2e-5,
    mieAnisotropy: 0.74,
    hazeColor: [0.42, 0.44, 0.5],
    windSpeed: 32,
    turbulence: 0.62,
    cloudCoverage: 0.34,
    cloudAltitude: 5200,
    cloudColor: [0.9, 0.9, 0.94],
  },
  thermal: {
    meanSurfaceTemperature: 254,
    polarDelta: 34,
    diurnalRange: 22,
    lapseRate: 0.0121,
  },
  hazards: {
    radiation: 0.00011,
    stormFrequency: 0.49,
    seismicActivity: 0.87,
    corrosion: 0.2,
    notes: [
      "Surface gravity of 2.3 g causes rapid fatigue and raises landing loads dramatically.",
      "Thrust-to-weight budgets must be recomputed before any ascent.",
      "Continuous tectonic activity along the equatorial ridge belt.",
    ],
  },
  terrain: {
    maxElevation: 4300,
    seaLevelFraction: -1,
    oceanCoverage: 0,
    continentScale: 3.2e6,
    plateCount: 41,
    ridgeStrength: 0.94,
    mountainStrength: 0.55,
    mountainScale: 1.4e5,
    warpStrength: 0.18,
    detailStrength: 0.4,
    detailScale: 600,
    erosion: 0.28,
    craterDensity: 0.08,
    craterMaxRadius: 26000,
    duneStrength: 0,
    duneScale: 1,
    fissureStrength: 0.34,
    roughness: 1.4,
  },
  palette: {
    deepWater: [0.06, 0.07, 0.09],
    shallowWater: [0.12, 0.14, 0.18],
    shore: [0.28, 0.28, 0.3],
    lowland: [0.21, 0.2, 0.22],
    midland: [0.3, 0.29, 0.31],
    highland: [0.42, 0.4, 0.42],
    peak: [0.58, 0.57, 0.6],
    nightEmissive: [0.6, 0.7, 1.0],
    nightEmissiveStrength: 0.1,
  },
  biomes: [
    "rockyDesert",
    "alpine",
    "crystalField",
    "cave",
    "radioactive",
    "glacier",
  ],
  settlementDensity: 0.16,
  lifePotential: 0.06,
  description:
    "A dense, seismically violent super-Earth. Everything about operating here is harder: landing gear rated for " +
    "Halcyon fails on touchdown, ascent delta-v is punishing, and unaugmented crews cannot work a full shift. " +
    "The reward is the richest titanium and rare-earth crust in the system.",
  markerColor: [0.56, 0.54, 0.58],
};

// ---------------------------------------------------------------------------
// 7. Meridia - dense jungle world
// ---------------------------------------------------------------------------
const MERIDIA: CelestialBodyDef = {
  id: "meridia",
  name: "Meridia",
  kind: "planet",
  parentId: "cyrene",
  radius: 6.68e6,
  surfaceGravity: 9.92,
  rotationPeriod: 31.2 * HOURS,
  axialTilt: 3.4 * DEG,
  epochSpin: 4.4,
  orbit: orbitAU(3.147, 0.0521, 1.1, 311.0, 67.0, 143.0),
  surfaceClass: "jungle",
  explorable: true,
  atmosphere: {
    surfaceDensity: 2.9,
    scaleHeight: 10800,
    surfacePressure: 262000,
    topAltitude: 138000,
    molarMass: 0.0322,
    composition: { N2: 0.68, CO2: 0.19, O2: 0.09, CH4: 0.04 },
    breathable: false,
    rayleigh: [7.1e-6, 1.6e-5, 2.4e-5],
    mie: 7.8e-5,
    mieAnisotropy: 0.81,
    hazeColor: [0.5, 0.66, 0.42],
    windSpeed: 5,
    turbulence: 0.34,
    cloudCoverage: 0.74,
    cloudAltitude: 2600,
    cloudColor: [0.98, 0.99, 1.0],
  },
  thermal: {
    meanSurfaceTemperature: 305,
    polarDelta: 22,
    diurnalRange: 8,
    lapseRate: 0.0049,
  },
  hazards: {
    radiation: 0.00005,
    stormFrequency: 0.72,
    seismicActivity: 0.24,
    corrosion: 0.9,
    notes: [
      "Methane fraction makes the lower atmosphere flammable near engine exhaust.",
      "Canopy reaches 90 m; ground-level visibility is under 40 m almost everywhere.",
      "Aggressive megafauna in the deep interior basins.",
      "Persistent humidity degrades unsealed electronics.",
    ],
  },
  terrain: {
    maxElevation: 5600,
    seaLevelFraction: -0.22,
    oceanCoverage: 0.41,
    continentScale: 1.6e6,
    plateCount: 12,
    ridgeStrength: 0.52,
    mountainStrength: 0.44,
    mountainScale: 2.1e5,
    warpStrength: 0.62,
    detailStrength: 0.3,
    detailScale: 1000,
    erosion: 0.88,
    craterDensity: 0,
    craterMaxRadius: 1,
    duneStrength: 0,
    duneScale: 1,
    fissureStrength: 0.06,
    roughness: 0.85,
  },
  palette: {
    deepWater: [0.02, 0.06, 0.06],
    shallowWater: [0.08, 0.26, 0.22],
    shore: [0.42, 0.4, 0.26],
    lowland: [0.07, 0.19, 0.06],
    midland: [0.11, 0.24, 0.08],
    highland: [0.24, 0.28, 0.16],
    peak: [0.44, 0.44, 0.38],
    nightEmissive: [0.32, 0.9, 0.6],
    nightEmissiveStrength: 0.28,
  },
  biomes: [
    "rainforest",
    "jungleCanopy",
    "swamp",
    "fungal",
    "alienOrganic",
    "ocean",
    "coastal",
    "cave",
    "karst",
  ],
  settlementDensity: 0.22,
  lifePotential: 1.0,
  description:
    "A warm, thick-aired world kept habitable this far out by a methane-carbon greenhouse. Its biosphere is the " +
    "densest known and almost entirely uncatalogued. Three separate surveys have found worked stone beneath the " +
    "canopy that no one has satisfactorily explained.",
  markerColor: [0.36, 0.72, 0.34],
};

// ---------------------------------------------------------------------------
// 8. Aegis - gas giant
// ---------------------------------------------------------------------------
const AEGIS: CelestialBodyDef = {
  id: "aegis",
  name: "Aegis",
  kind: "gasGiant",
  parentId: "cyrene",
  radius: 6.42e7,
  surfaceGravity: 24.6,
  rotationPeriod: 9.84 * HOURS,
  axialTilt: 2.9 * DEG,
  epochSpin: 1.7,
  orbit: orbitAU(5.402, 0.0471, 1.3, 100.0, 274.0, 78.0),
  surfaceClass: "none",
  explorable: false,
  atmosphere: {
    surfaceDensity: 0.16,
    scaleHeight: 27000,
    surfacePressure: 100000,
    topAltitude: 640000,
    molarMass: 0.0023,
    composition: { H2: 0.88, He: 0.1, CH4: 0.017, NH3: 0.003 },
    breathable: false,
    rayleigh: [4.2e-6, 8.1e-6, 1.9e-5],
    mie: 6.4e-5,
    mieAnisotropy: 0.84,
    hazeColor: [0.78, 0.68, 0.52],
    windSpeed: 140,
    turbulence: 0.9,
    cloudCoverage: 0.95,
    cloudAltitude: 60000,
    cloudColor: [0.94, 0.88, 0.76],
  },
  thermal: {
    meanSurfaceTemperature: 132,
    polarDelta: 8,
    diurnalRange: 2,
    lapseRate: 0.002,
  },
  hazards: {
    radiation: 0.42,
    stormFrequency: 0.96,
    seismicActivity: 0,
    corrosion: 0.4,
    notes: [
      "Magnetospheric radiation is lethal inside 4 planetary radii without shielding.",
      "No solid surface: any descent below the 1 bar level is one-way.",
      "Helium-3 skimming is profitable but requires continuous thrust to avoid capture.",
    ],
  },
  terrain: null,
  palette: {
    deepWater: [0, 0, 0],
    shallowWater: [0, 0, 0],
    shore: [0, 0, 0],
    lowland: [0.72, 0.62, 0.48],
    midland: [0.8, 0.71, 0.56],
    highland: [0.66, 0.56, 0.46],
    peak: [0.88, 0.82, 0.7],
    nightEmissive: [0.2, 0.4, 0.9],
    nightEmissiveStrength: 0.06,
  },
  biomes: [],
  rings: {
    innerRadius: 8.4e7,
    outerRadius: 1.42e8,
    color: [0.76, 0.7, 0.6],
    opacity: 0.55,
  },
  settlementDensity: 0.28,
  lifePotential: 0.02,
  description:
    "A banded hydrogen giant with a thin, sharply defined ring system and a family of geologically varied moons. " +
    "Its gravity well is the system's most useful navigational feature: nearly every efficient outer-system " +
    "transfer routes through an Aegis flyby.",
  markerColor: [0.82, 0.72, 0.52],
};

// ---------------------------------------------------------------------------
// 9. Kolyma - frozen outer world
// ---------------------------------------------------------------------------
const KOLYMA: CelestialBodyDef = {
  id: "kolyma",
  name: "Kolyma",
  kind: "planet",
  parentId: "cyrene",
  radius: 5.44e6,
  surfaceGravity: 6.12,
  rotationPeriod: 17.9 * HOURS,
  axialTilt: 62.1 * DEG,
  epochSpin: 2.8,
  orbit: orbitAU(9.148, 0.1124, 7.6, 22.0, 198.0, 340.0),
  surfaceClass: "frozen",
  explorable: true,
  atmosphere: {
    surfaceDensity: 0.14,
    scaleHeight: 18200,
    surfacePressure: 6400,
    topAltitude: 110000,
    molarMass: 0.028,
    composition: { N2: 0.96, CH4: 0.03, Ar: 0.01 },
    breathable: false,
    rayleigh: [1.4e-5, 1.7e-5, 2.6e-5],
    mie: 1.1e-5,
    mieAnisotropy: 0.68,
    hazeColor: [0.62, 0.72, 0.86],
    windSpeed: 4,
    turbulence: 0.2,
    cloudCoverage: 0.3,
    cloudAltitude: 5600,
    cloudColor: [0.88, 0.92, 0.98],
  },
  thermal: {
    meanSurfaceTemperature: 96,
    polarDelta: 18,
    diurnalRange: 4,
    lapseRate: 0.0021,
  },
  hazards: {
    radiation: 0.00008,
    stormFrequency: 0.16,
    seismicActivity: 0.11,
    corrosion: 0,
    notes: [
      "Cryogenic surface temperatures embrittle standard alloys.",
      "Extreme axial tilt gives multi-year polar night across most of the surface.",
      "Nitrogen frost sublimation causes sudden local pressure changes at dawn.",
    ],
  },
  terrain: {
    maxElevation: 3200,
    seaLevelFraction: -1,
    oceanCoverage: 0,
    continentScale: 2.9e6,
    plateCount: 6,
    ridgeStrength: 0.3,
    mountainStrength: 0.25,
    mountainScale: 3.8e5,
    warpStrength: 0.24,
    detailStrength: 0.18,
    detailScale: 1800,
    erosion: 0.2,
    craterDensity: 0.66,
    craterMaxRadius: 88000,
    duneStrength: 22,
    duneScale: 4400,
    fissureStrength: 0.28,
    roughness: 0.7,
  },
  palette: {
    deepWater: [0.05, 0.09, 0.16],
    shallowWater: [0.16, 0.28, 0.4],
    shore: [0.62, 0.68, 0.74],
    lowland: [0.72, 0.78, 0.86],
    midland: [0.8, 0.85, 0.92],
    highland: [0.66, 0.72, 0.82],
    peak: [0.93, 0.96, 1.0],
    nightEmissive: [0.5, 0.7, 1.0],
    nightEmissiveStrength: 0.09,
  },
  biomes: ["glacier", "iceField", "tundra", "cave", "crystalField", "saltFlat"],
  settlementDensity: 0.09,
  lifePotential: 0.12,
  description:
    "A nitrogen-frosted ice world tipped almost onto its side. Its volatile inventory makes it the cheapest " +
    "refuelling stop in the outer system, and the long polar nights have preserved surface features — and, in " +
    "three catalogued locations, structures — for a very long time.",
  markerColor: [0.7, 0.84, 0.96],
};

export const PLANETS: CelestialBodyDef[] = [
  VESPER,
  ASHKAR,
  HALCYON,
  THALASSA,
  KHARIS,
  ORPHYS,
  MERIDIA,
  AEGIS,
  KOLYMA,
];

export const HOME_PLANET_ID = "halcyon";
